<script setup lang="ts">
import { useBillingUtils } from '@/use/billingUtils';
import { useI18n } from '@/i18n';
import { logError } from '@/lib/logError';
import { useBillingStore } from '@/stores/billing';
import { LayoutGap } from '@dabble/quillui';
import type { Stripe } from 'stripe';
import { computed, onMounted, ref, watch } from 'vue';
import AppSkeleton from '@/components/AppSkeleton/AppSkeleton.vue';

const props = defineProps<{ class?: string }>();

const billingStore = useBillingStore();
const { formatPrice, upcomingInvoicePaymentString } = useBillingUtils();
const { t } = useI18n();

const upcomingInvoiceData = ref<Stripe.Invoice | null>(null);
const loadingUpcoming = ref(false);

const customer = computed(() => billingStore.customer as Stripe.Customer | null);

const subscription = computed(() => billingStore.primarySubscription);

const isLifetime = computed(() => billingStore.isLifetime);

const className = computed(() => props.class || '');

const stripeBalance = computed(() => customer.value?.balance ?? 0);

const hasNonZeroBalance = computed(() => stripeBalance.value !== 0);

const balanceCurrency = computed(
  () => (customer.value?.currency || upcomingInvoiceData.value?.currency || 'usd') as string,
);

const balanceValueSuffix = computed(() => {
  const b = stripeBalance.value;
  if (b === 0) return '';
  if (b < 0) return t('billing_balance_credit_word');
  return t('billing_balance_owed_word');
});

const balanceAmountWithSuffix = computed(() => {
  const amt = formatBalance(stripeBalance.value, balanceCurrency.value);
  const suf = balanceValueSuffix.value;
  return suf ? `${amt} ${suf}` : amt;
});

const upcomingZeroChargeNote = computed(() => {
  const inv = upcomingInvoiceData.value;
  if (!inv || inv.amount_due !== 0) return '';
  return t('billing_upcoming_no_charge_detail');
});

const showBalanceRow = computed(() => hasNonZeroBalance.value);

const showNextPaymentRow = computed(() => {
  const sub = subscription.value;
  if (!sub) return false;
  if (sub.cancel_at_period_end) return false;
  return (
    sub.status === 'active' ||
    sub.status === 'trialing' ||
    sub.status === 'past_due' ||
    sub.status === 'paused' ||
    sub.status === 'unpaid'
  );
});

const showSection = computed(
  () => billingStore.hasStripeCustomer && (showBalanceRow.value || showNextPaymentRow.value),
);

async function loadNextInvoice() {
  if (!billingStore.hasStripeCustomer || !showNextPaymentRow.value || isLifetime.value) {
    upcomingInvoiceData.value = null;
    // We're the only caller of fetchUpcomingInvoice, so also clear the store-level
    // invoice that BillingDiscountAlert reads for proration/credit fallback.
    billingStore.upcomingInvoice = null;
    return;
  }
  loadingUpcoming.value = true;
  try {
    upcomingInvoiceData.value = await billingStore.fetchUpcomingInvoice();
  } catch (err) {
    logError('Failed to load upcoming invoice:', err);
    upcomingInvoiceData.value = null;
  } finally {
    loadingUpcoming.value = false;
  }
}

function formatBalance(balance: number, currency: string): string {
  return formatPrice(Math.abs(balance), currency, true);
}

watch(
  () => [subscription.value?.id, subscription.value?.cancel_at_period_end, showNextPaymentRow.value, isLifetime.value],
  () => {
    loadNextInvoice();
  },
);

onMounted(() => {
  loadNextInvoice();
});
</script>

<template>
  <div v-if="showSection" :class="['billing-upcoming-invoice-summary', className]">
    <div class="upcoming-billing-summary__inner">
      <div v-if="showBalanceRow" class="upcoming-billing-summary__row">
        <span class="upcoming-billing-summary__label">{{ t('billing_balance') }}</span>
        <span class="upcoming-billing-summary__value">{{ balanceAmountWithSuffix }}</span>
      </div>

      <div v-if="showNextPaymentRow" class="upcoming-billing-summary__row">
        <span class="upcoming-billing-summary__label">{{ t('billing_next_payment') }}</span>
        <LayoutGap direction="column" size="none" class="upcoming-billing-summary__next">
          <span v-if="isLifetime" class="upcoming-billing-summary__next-main">{{
            t('billing_lifetime_next_payment')
          }}</span>
          <span v-else-if="loadingUpcoming" class="upcoming-billing-summary__loading">
            <AppSkeleton width="14rem" height="0.85rem" />
          </span>
          <template v-else-if="upcomingInvoiceData">
            <span class="upcoming-billing-summary__next-main">{{
              upcomingInvoicePaymentString(upcomingInvoiceData as Stripe.Invoice)
            }}</span>
            <span v-if="upcomingZeroChargeNote" class="upcoming-billing-summary__next-sub">{{
              upcomingZeroChargeNote
            }}</span>
          </template>
          <span v-else>{{ t('billing_next_payment_none') }}</span>
        </LayoutGap>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// Renders as the billing portal's small-caps "stat" blocks (label over value),
// flowing inline with the Member Since stat in the plan card's meta band.
.billing-upcoming-invoice-summary {
  color: theme('color-text-primary');
}

.upcoming-billing-summary__inner {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: space('small') space('large');
  font-size: fontSize('small');
}

.upcoming-billing-summary__row {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.upcoming-billing-summary__label {
  font-size: fontSize('xxsmall');
  font-weight: fontWeight('bold');
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: theme('color-text-placeholder');
}

.upcoming-billing-summary__value {
  text-align: left;
  font-weight: fontWeight('semibold');
  color: theme('color-text-primary');
}

.upcoming-billing-summary__next {
  align-items: flex-start;
  text-align: left;
  min-width: 0;
}

.upcoming-billing-summary__loading {
  display: inline-flex;
  align-items: center;
  min-height: 1.2em;
}

.upcoming-billing-summary__next-main {
  display: block;
  font-weight: fontWeight('semibold');
}

.upcoming-billing-summary__next-sub {
  display: block;
  max-width: 40ch;
  font-size: fontSize('xsmall');
  line-height: 1.35;
  color: theme('color-text-secondary');
}
</style>
